import type { Data, FieldClassification } from "@eai-toolkit/client";
import { Edit } from "@mui/icons-material";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { useUpdateDataObjectMutation } from "@store/api/dataApi";
import { clearCache, updateDataObjectInList } from "@store/slices/dataSlice";
import React, { useState } from "react";
import { useDispatch } from "react-redux";

import {
  CLASSIFICATION_METADATA,
  type NonEmptyFieldClassification,
} from "../../../data/fieldRenderers/Classification";
import type { RowAction } from "../../../data/types";

const CLASSIFICATION_OPTIONS: Array<{
  value: FieldClassification | "";
  label: string;
  tooltip: string;
}> = [
  { value: "", label: "-", tooltip: "No classification" },
  ...Object.entries(CLASSIFICATION_METADATA).map(([value, meta]) => ({
    value: value as FieldClassification,
    ...meta,
  })),
];

interface EditClassificationDialogProps {
  open: boolean;
  onClose: () => void;
  dataObject: Data;
}

function EditClassificationDialog({
  open,
  onClose,
  dataObject,
}: EditClassificationDialogProps) {
  const [value, setValue] = useState<FieldClassification | "">(
    (dataObject.classification as FieldClassification) || "",
  );
  const [updateDataObject, { isLoading }] = useUpdateDataObjectMutation();
  const dispatch = useDispatch();
  const [error, setError] = useState<string>("");

  const handleSave = async () => {
    const previousValue =
      (dataObject.classification as FieldClassification) || "";

    // Validate that data has a fullName
    if (!dataObject.fullName) {
      setError("Cannot update classification for data without a name");
      return;
    }

    // Clear any previous errors
    setError("");

    try {
      const updatePayload = {
        id: dataObject.id,
        classification: value as FieldClassification,
      };

      // Optimistically update the local state in Redux store
      dispatch(
        updateDataObjectInList({
          id: dataObject.id,
          updates: { classification: value as FieldClassification },
        }),
      );

      await updateDataObject(updatePayload).unwrap();

      // Clear cache after successful update to ensure sorting fetches fresh data
      dispatch(clearCache());

      onClose();
    } catch (error) {
      console.error("Error updating classification:", error);
      // Revert both local state and Redux store on error
      setValue(previousValue);
      dispatch(
        updateDataObjectInList({
          id: dataObject.id,
          updates: { classification: previousValue },
        }),
      );
      setError("Failed to update classification. Please try again.");
    }
  };

  const handleCancel = () => {
    setValue((dataObject.classification as FieldClassification) || "");
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="xs" fullWidth>
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Data: {dataObject.fullName || dataObject.id}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Select
          value={value}
          onChange={(e) => setValue(e.target.value as FieldClassification | "")}
          disabled={isLoading}
          size="small"
          fullWidth
          displayEmpty
        >
          {CLASSIFICATION_OPTIONS.map((option) => (
            <MenuItem key={option.value || "empty"} value={option.value}>
              <Tooltip title={option.tooltip} placement="right" arrow>
                <span style={{ width: "100%" }}>{option.label}</span>
              </Tooltip>
            </MenuItem>
          ))}
        </Select>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: "block" }}
        >
          {value &&
            CLASSIFICATION_METADATA[value as NonEmptyFieldClassification]
              ?.tooltip}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isLoading}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function useEditClassificationAction(): {
  action: RowAction<Data>;
  dialog: React.ReactNode;
} {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedData, setSelectedData] = useState<Data | null>(null);

  const action: RowAction<Data> = {
    label: "Edit classification",
    getAction: (row: Data) => ({
      onClick: () => {
        setSelectedData(row);
        setDialogOpen(true);
      },
    }),
    icon: (disabled: boolean) => (
      <Edit fontSize="small" color={disabled ? "disabled" : "action"} />
    ),
  };

  const dialog = selectedData ? (
    <EditClassificationDialog
      open={dialogOpen}
      onClose={() => {
        setDialogOpen(false);
        setSelectedData(null);
      }}
      dataObject={selectedData}
    />
  ) : null;

  return { action, dialog };
}
